import { Api } from '../../Api';
import { FieldValues } from '@cjdev-internal/visual-stack-x/CJForm';
import { NavigateFunction } from 'react-router-dom';
import { ToastOptions } from '@cjdev-internal/visual-stack-x/Toast';
import * as R from 'ramda';
import intl from '../../utils/intl';
import { content } from './message';
import {
  keyField,
  partnerField,
  PartnershipGroups,
  PartnershipResult,
  ProcessContext,
  statusField,
} from './partnershipPageTypes';
import {
  Mode,
  Partner,
  PartnerConfigFor,
  PartnerName,
  partnershipDefaults,
} from '@callback-cats/partnerships-ts/Partnership';
import { buildMappedObject, uncapitalize } from '@callback-cats/ts-helpers/util';

export const managePartnerships = async (
  api: Api,
  existingPartnerships: Partner[],
  tagId: string,
  cid: string,
  data: FieldValues,
  rowCount: number,
  navigate: NavigateFunction,
  showToast: (options: ToastOptions) => string
): Promise<void> => {
  const partnershipsDataFromForm = extractPartnershipsFromForm(data, rowCount);

  const { newPartnerships, updatedPartnerships } = categorizePartnerships(
    partnershipsDataFromForm,
    existingPartnerships
  );

  const processContext: ProcessContext = { api, cid, tagId };

  const results = await Promise.all([
    ...processNewPartnerships(processContext, newPartnerships),
    ...processUpdatedPartnerships(processContext, updatedPartnerships),
  ]);

  handleResults(results, navigate, showToast);
};

// Categorize partnerships into new and updated
const categorizePartnerships = (
  currentPartnerships: Partner[],
  existingPartnerships: Partner[]
): PartnershipGroups => {
  const newPartnerships: Partner[] = [];
  const updatedPartnerships: Partner[] = [];

  currentPartnerships.forEach((currentPartnership, index) => {
    // Check if this is a new partnership
    if (index >= existingPartnerships.length) {
      newPartnerships.push(currentPartnership);
      return;
    }

    // Check if the existing partnership has changed
    const existingPartnership = existingPartnerships[index];
    if (!R.equals(currentPartnership, existingPartnership)) {
      updatedPartnerships.push(currentPartnership);
    }
  });

  return { newPartnerships, updatedPartnerships };
};

// Process new partnerships
const processNewPartnerships = (
  context: ProcessContext,
  newPartnerships: Partner[]
): Promise<PartnershipResult>[] => {
  return newPartnerships.map((partnership) =>
    createPartnership(context, partnership)
  );
};

// Process partnerships that need updating
const processUpdatedPartnerships = (
  context: ProcessContext,
  updatedPartnerships: Partner[]
): Promise<PartnershipResult>[] => {
  return updatedPartnerships.map((partnership) => {
    return updatePartnership(context, partnership);
  });
};

// Create a new partnership
const createPartnership = async (
  context: ProcessContext,
  partnership: Partner
): Promise<PartnershipResult> => {
  try {
    await context.api.createPartnership(
      context.cid,
      { id: context.tagId },
      partnership
    );
    return {
      success: true,
      message: formatSuccessMessage(partnership.name),
      partnership,
    };
  } catch (error) {
    return {
      success: false,
      message: formatFailureMessage(partnership.name, error as Error),
      partnership,
    };
  }
};

// Update an existing partnership
const updatePartnership = async (
  context: ProcessContext,
  partnership: Partner
): Promise<PartnershipResult> => {
  try {
    await context.api.editPartnership(
      context.cid,
      { id: context.tagId },
      partnership
    );
    return {
      success: true,
      message: formatSuccessMessage(partnership.name),
      partnership,
    };
  } catch (error) {
    return {
      success: false,
      message: formatFailureMessage(partnership.name, error as Error),
      partnership,
    };
  }
};

const handleResults = (
  results: PartnershipResult[],
  navigate: NavigateFunction,
  showToast: (options: ToastOptions) => string
): void => {
  const successful = results.filter((r) => r.success);
  const failed = results.filter((r) => !r.success);

  if (failed.length === 0) {
    navigate('/list', { state: successful.map((r) => r.message) });
  } else {
    failed.forEach((r) => showToast({ message: r.message, type: 'warning' }));
    successful.forEach((r) =>
      showToast({ message: r.message, type: 'success' })
    );
  }
};

const formatSuccessMessage = (name: string): string =>
  `${intl.formatMessage(content.partnershipSaveSuccessAlert)} ${name}.`;

const formatFailureMessage = (name: string, error: Error): string =>
  `${intl.formatMessage(content.partnershipSaveFailAlert)} ${name}. ${
    error.message
  }`;

function extractPartnershipsFromForm(
  formValues: FieldValues,
  rowCount: number
): Partner[] {
  return [...Array(rowCount).keys()].map((rowIndex: number): Partner => {
    const partnerName: PartnerName =
      formValues[partnerField + rowIndex.toString()];
    const mode: Mode = formValues[statusField + rowIndex.toString()];

    const partnerDefault = partnershipDefaults[uncapitalize(partnerName)];
    const dataKeys = Object.keys(
      partnerDefault.data
    ) as (keyof PartnerConfigFor<typeof partnerName>['data'])[];

    const data: PartnerConfigFor<typeof partnerName>['data'] =
      buildMappedObject<
        keyof PartnerConfigFor<typeof partnerName>['data'],
        PartnerConfigFor<typeof partnerName>['data']
      >(
        dataKeys,
        (key) => key,
        (key) =>
          key === 'key'
            ? formValues[keyField + rowIndex.toString()]
            : formValues[key + rowIndex.toString()]
      );

    const partnerData: Omit<PartnerConfigFor<typeof partnerName>, 'enabled'> = {
      name: partnerName,
      mode,
      data,
    };
    return partnerData as Partner;
  });
}
